import axios from 'axios';
import { postData } from '../../services/axiosUtils';

const asListResponse = (data: any) => {
  if (Array.isArray(data)) {
    return { hasError: false, records: data, errorMessages: [] };
  }
  if (data?.records) {
    return data;
  }
  return { hasError: false, records: data ? [data] : [], errorMessages: [] };
};

const wrapEntity = (data: any) => {
  if (data?.hasError !== undefined) {
    return data;
  }
  return { hasError: false, result: data, errorMessages: [] };
};

const normalize = (row: any) => ({
  id: row.id ?? row.Id,
  name: row.name ?? row.Name,
  slug: row.slug ?? row.Slug,
  description: row.description ?? row.Description,
  isActive: row.isActive ?? row.IsActive,
  suggestionMode: row.suggestionMode ?? row.SuggestionMode ?? 0,
  createdAt: row.createdAt ?? row.CreatedAt,
  modifiedAt: row.modifiedAt ?? row.ModifiedAt,
  memberCount: row.memberCount ?? row.MemberCount ?? 0,
  members: (row.members ?? row.Members ?? []).map((m: any) => ({
    id: m.id ?? m.Id,
    tenantId: m.tenantId ?? m.TenantId,
    tenantName: m.tenantName ?? m.TenantName,
    tenantSlug: m.tenantSlug ?? m.TenantSlug,
    displayName: m.displayName ?? m.DisplayName,
    deepLinkSlug: m.deepLinkSlug ?? m.DeepLinkSlug,
    sortOrder: m.sortOrder ?? m.SortOrder ?? 0,
    isDefaultCandidate: m.isDefaultCandidate ?? m.IsDefaultCandidate ?? false,
    shortcutIconUrl: m.shortcutIconUrl ?? m.ShortcutIconUrl ?? '',
  })),
});

const tenantGroupService = {
  list: () =>
    axios.get(`/api/tenant-groups/list`).then((response) => {
      const wrapped = asListResponse(response.data);
      return {
        ...wrapped,
        records: (wrapped.records ?? []).map(normalize),
      };
    }),
  get: (id: string) =>
    axios.get(`/api/tenant-groups/details/${id}`).then((response) =>
      wrapEntity(normalize(response.data))
    ),
  create: (entity: any) =>
    postData({
      url: `/api/tenant-groups/create`,
      data: {
        name: entity.name,
        slug: entity.slug,
        description: entity.description,
        isActive: entity.isActive !== false,
        suggestionMode: entity.suggestionMode ?? 0,
      },
    }).then((response) => wrapEntity(normalize(response))),
  update: (id: string, entity: any) =>
    postData({
      url: `/api/tenant-groups/update/${id}`,
      method: 'PUT',
      data: {
        name: entity.name,
        slug: entity.slug,
        description: entity.description,
        isActive: entity.isActive !== false,
        suggestionMode: entity.suggestionMode ?? 0,
      },
    }).then((response) => wrapEntity(normalize(response))),
  delete: (id: string) =>
    postData({
      url: `/api/tenant-groups/delete/${id}`,
      method: 'DELETE',
    }),
  addMember: (groupId: string, member: any) =>
    postData({
      url: `/api/tenant-groups/${groupId}/members`,
      data: {
        tenantId: member.tenantId,
        displayName: member.displayName,
        deepLinkSlug: member.deepLinkSlug,
        sortOrder: member.sortOrder ?? 0,
        isDefaultCandidate: !!member.isDefaultCandidate,
        shortcutIconUrl: member.shortcutIconUrl || null,
      },
    }).then((response) => wrapEntity(response)),
  updateMember: (groupId: string, memberId: string, member: any) =>
    postData({
      url: `/api/tenant-groups/${groupId}/members/${memberId}`,
      method: 'PUT',
      data: {
        tenantId: member.tenantId,
        displayName: member.displayName,
        deepLinkSlug: member.deepLinkSlug,
        sortOrder: member.sortOrder ?? 0,
        isDefaultCandidate: !!member.isDefaultCandidate,
        shortcutIconUrl: member.shortcutIconUrl || null,
      },
    }).then((response) => wrapEntity(response)),
  deleteMember: (groupId: string, memberId: string) =>
    postData({
      url: `/api/tenant-groups/${groupId}/members/${memberId}`,
      method: 'DELETE',
    }),
};

export default tenantGroupService;
